import Vue from 'vue'
import VueI18n from 'vue-i18n'
import enUS from './locales/en-US.json'
import zhCN from './locales/zh-CN'
import zhTW from './locales/zh-TW.json'
import esES from './locales/es-ES.json'
import ruRU from './locales/ru-RU.json'
import frFR from './locales/fr-FR.json'
import msMY from './locales/ms-MY.json'
import thTH from './locales/th-TH.json'
import viVN from './locales/vi-VN.json'
import kmKH from './locales/km-KH.json'
import idID from './locales/id-ID.json'
import jaJP from './locales/ja-JP.json'
import arSA from './locales/ar-SA.json'
import deDE from './locales/de-DE.json'
import { DEFAULT_TITLE } from './pageTitles'

Vue.use(VueI18n)

export const SUPPORTED_LOCALES = [{
    code: 'zh-CN',
    label: '简体中文',
    shortLabel: '中文',
    dir: 'ltr',
    enabled: true
}, {
    code: 'zh-TW',
    label: '繁體中文',
    shortLabel: '繁中',
    dir: 'ltr',
    enabled: true
}, {
    code: 'en-US',
    label: 'English',
    shortLabel: 'EN',
    dir: 'ltr',
    enabled: true
}, {
    code: 'es-ES',
    label: 'Español',
    shortLabel: 'ES',
    dir: 'ltr',
    enabled: true
}, {
    code: 'ru-RU',
    label: 'Русский',
    shortLabel: 'RU',
    dir: 'ltr',
    enabled: true
}, {
    code: 'fr-FR',
    label: 'Français',
    shortLabel: 'FR',
    dir: 'ltr',
    enabled: true
}, {
    code: 'ms-MY',
    label: 'Bahasa Melayu',
    shortLabel: 'MS',
    dir: 'ltr',
    enabled: true
}, {
    code: 'th-TH',
    label: 'ไทย',
    shortLabel: 'TH',
    dir: 'ltr',
    enabled: true
}, {
    code: 'vi-VN',
    label: 'Tiếng Việt',
    shortLabel: 'VI',
    dir: 'ltr',
    enabled: true
}, {
    code: 'km-KH',
    label: 'ខ្មែរ',
    shortLabel: 'KM',
    dir: 'ltr',
    enabled: true
}, {
    code: 'id-ID',
    label: 'Bahasa Indonesia',
    shortLabel: 'ID',
    dir: 'ltr',
    enabled: true
}, {
    code: 'ja-JP',
    label: '日本語',
    shortLabel: 'JA',
    dir: 'ltr',
    enabled: true
}, {
    code: 'ar-SA',
    label: 'العربية',
    shortLabel: 'AR',
    dir: 'ltr',
    enabled: true
}, {
    code: 'de-DE',
    label: 'Deutsch',
    shortLabel: 'DE',
    dir: 'ltr',
    enabled: true
}]

export const ENABLED_LOCALES = SUPPORTED_LOCALES.filter(item => item.enabled !== false)

const DEFAULT_LOCALE = 'zh-CN'
const FALLBACK_LOCALE = 'en-US'
const STORAGE_KEY = 'fp_locale'

function matchLocale(value) {
    if (!value) return ''
    const normalized = String(value).replace('_', '-').toLowerCase()
    const exact = ENABLED_LOCALES.find(item => item.code.toLowerCase() === normalized)
    if (exact) return exact.code
    const base = normalized.split('-')[0]
    if (base === 'zh') {
        const traditional = /hant|tw|hk|mo/.test(normalized)
        return traditional ? 'zh-TW' : 'zh-CN'
    }
    const matched = ENABLED_LOCALES.find(item => item.code.toLowerCase().split('-')[0] === base)
    return matched ? matched.code : ''
}

function getQueryLocale() {
    const match = window.location.search.match(/[?&]lang=([^&]+)/)
    return match ? decodeURIComponent(match[1]) : ''
}

function getSavedLocale() {
    try {
        return window.localStorage.getItem(STORAGE_KEY) || ''
    } catch (e) {
        return ''
    }
}

function saveLocale(locale) {
    try {
        window.localStorage.setItem(STORAGE_KEY, locale)
    } catch (e) {
        // ignore storage errors
    }
}

function detectLocale() {
    const fromQuery = matchLocale(getQueryLocale())
    if (fromQuery) return fromQuery

    const fromStorage = matchLocale(getSavedLocale())
    if (fromStorage) return fromStorage

    const browserLocale = (navigator.languages && navigator.languages[0]) || navigator.language || ''
    const fromBrowser = matchLocale(browserLocale)
    if (fromBrowser) return fromBrowser

    return DEFAULT_LOCALE
}

export function normalizeLocale(locale) {
    return matchLocale(locale) || DEFAULT_LOCALE
}

export function getLocaleMeta(locale) {
    return SUPPORTED_LOCALES.find(item => item.code === locale) || SUPPORTED_LOCALES[0]
}

function langUrl(locale) {
    const href = window.location.href
    const hashIndex = href.indexOf('#')
    const hash = hashIndex >= 0 ? href.slice(hashIndex) : ''
    const base = hashIndex >= 0 ? href.slice(0, hashIndex) : href
    const queryIndex = base.indexOf('?')
    const path = queryIndex >= 0 ? base.slice(0, queryIndex) : base
    const query = queryIndex >= 0 ? base.slice(queryIndex + 1) : ''
    const params = query.split('&').filter(item => item && !/^lang=/.test(item))
    params.push('lang=' + encodeURIComponent(locale))
    return path + '?' + params.join('&') + hash
}

export function setLocale(locale) {
    const next = normalizeLocale(locale)
    saveLocale(next)
    window.location.replace(langUrl(next))
}

function setMeta(name, content) {
    let element = document.querySelector('meta[name="' + name + '"]')
    if (!element) {
        element = document.createElement('meta')
        element.setAttribute('name', name)
        document.head.appendChild(element)
    }
    element.setAttribute('content', content)
}

export function applyDocumentLocale() {
    const locale = i18n.locale
    const meta = getLocaleMeta(locale)
    document.documentElement.lang = locale
    document.documentElement.dir = meta.dir
    document.title = i18n.t(DEFAULT_TITLE) + ' | ' + i18n.t('丰派软件')
    if (locale !== 'zh-CN') {
        setMeta('description', i18n.t('丰派为商户提供收银、自助收银、微商城、智慧零售、订货平台等数字化解决方案。'))
        setMeta('keywords', i18n.t('收银,收银系统,零售,微商城,智慧零售,订货平台,FengPai'))
    }
}

const initialLocale = detectLocale()
saveLocale(initialLocale)

export const i18n = new VueI18n({
    locale: initialLocale,
    fallbackLocale: FALLBACK_LOCALE,
    messages: {
        'zh-CN': zhCN,
        'zh-TW': zhTW,
        'en-US': enUS,
        'es-ES': esES,
        'ru-RU': ruRU,
        'fr-FR': frFR,
        'ms-MY': msMY,
        'th-TH': thTH,
        'vi-VN': viVN,
        'km-KH': kmKH,
        'id-ID': idID,
        'ja-JP': jaJP,
        'ar-SA': arSA,
        'de-DE': deDE
    },
    silentFallbackWarn: true,
    silentTranslationWarn: true,
    missing: (locale, key) => key
})

export function t(key, values) {
    return i18n.t(key, values)
}

export default i18n



// WEBPACK FOOTER //
// ./src/i18n/index.js